import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";

const membershipConfig = {
  "Family Membership": {
    label: "Family Membership - $40",
    amount: "$40",
    adults: 2,
    kids: 2,
    requiresIdForPrimaryAdult: false,
    requiresStudentId: false,
  },
  "Single Adult Membership": {
    label: "Single Adult Membership - $30",
    amount: "$30",
    adults: 1,
    kids: 0,
    requiresIdForPrimaryAdult: true,
    requiresStudentId: false,
  },
  "Student Membership": {
    label: "Student Membership - $20",
    amount: "$20",
    adults: 1,
    kids: 0,
    requiresIdForPrimaryAdult: false,
    requiresStudentId: true,
  },
  "Senior Couple Membership": {
    label: "Senior Couple Membership - $25",
    amount: "$25",
    adults: 2,
    kids: 0,
    requiresIdForPrimaryAdult: false,
    requiresStudentId: false,
  },
  "Family One Adult": {
    label: "One Adult + Children - $25",
    amount: "$25",
    adults: 1,
    kids: 2,
    requiresIdForPrimaryAdult: true,
    requiresStudentId: false,
  },
  "Senior Single Membership": {
    label: "Senior Single Membership - $15",
    amount: "$15",
    adults: 1,
    kids: 0,
    requiresIdForPrimaryAdult: true,
    requiresStudentId: false,
  },
};

const createAdult = () => ({
  fullName: "",
  dob: "",
  phone: "",
  relation: "",
  idProof: null,
  studentIdProof: null,
});

const createChild = () => ({
  fullName: "",
  dob: "",
  relation: "Child",
});

export default function MembershipForm() {
  const navigate = useNavigate();
  const location = useLocation();

  /* Edit form */

  const editMode = location.state?.editMode || false;
  const applicationId = location.state?.applicationId || null;

  useEffect(() => {
    const loadApplicationForEdit = async () => {
      if (!editMode || !applicationId) return;

      try {
        const result = await api.post("getMembershipApplicationById.php", {
          id: applicationId,
        });

        if (!result?.success) {
          alert(result?.message || "Failed to load application");
          return;
        }

        const app = result.application;
        const people = result.people || [];

        const adults = people
          .filter((person) => person.person_type === "Adult")
          .map((person) => ({
            fullName: person.full_name || "",
            dob: person.dob || "",
            phone: person.phone || "",
            relation: person.relation || "",
            idProof: person.id_proof || null,
            studentIdProof: person.student_id_proof || null,
          }));

        const kids = people
          .filter((person) => person.person_type === "Child")
          .map((person) => ({
            fullName: person.full_name || "",
            dob: person.dob || "",
            relation: person.relation || "Child",
          }));

        setFormData({
          membershipType: app.membership_type || "",
          mainContactName: app.main_contact_name || "",
          email: app.email || "",
          phone: app.phone || "",
          message: app.notes || "",
          adults,
          kids,
        });
      } catch (error) {
        console.error("Edit load error:", error);
        alert("Something went wrong while loading application");
      }
    };

    loadApplicationForEdit();
  }, [editMode, applicationId]);

  /* Edit form */

  const [accountHolder, setAccountHolder] = useState(null);

  const [formData, setFormData] = useState({
    membershipType: location.state?.membershipType || "",
    mainContactName: "",
    email: "",
    phone: "",
    message: "",
    adults: [],
    kids: [],
  });

  const currentConfig = useMemo(
    () => membershipConfig[formData.membershipType],
    [formData.membershipType]
  );

  useEffect(() => {
    const savedMember = localStorage.getItem("member");

    if (!savedMember) {
      navigate("/login");
      return;
    }

    const parsedMember = JSON.parse(savedMember);
    setAccountHolder(parsedMember);

    setFormData((prev) => ({
      ...prev,
      mainContactName: parsedMember.full_name || "",
      email: parsedMember.email || "",
      phone: parsedMember.phone || "",
    }));
  }, [navigate]);

  useEffect(() => {
    if (!formData.membershipType || editMode) return;

    const config = membershipConfig[formData.membershipType];
    if (!config) return;

    setFormData((prev) => ({
      ...prev,
      adults: Array.from({ length: config.adults }, () => createAdult()),
      kids: Array.from({ length: config.kids }, () => createChild()),
    }));
  }, [formData.membershipType, editMode]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateAdult = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.adults];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, adults: updated };
    });
  };

  const updateChild = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.kids];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, kids: updated };
    });
  };

  const addChild = () => {
    setFormData((prev) => ({
      ...prev,
      kids: [...prev.kids, createChild()],
    }));
  };

  const removeChild = (index) => {
    setFormData((prev) => {
      const updated = [...prev.kids];
      updated.splice(index, 1);
      return { ...prev, kids: updated };
    });
  };

  // Local storage is used here for simplicity. In a real application, this would be sent to a backend server.
  /* const handleSubmit = (e) => {
    e.preventDefault();

    const application = {
      id: Date.now(),
      accountHolderId: accountHolder?.id || null,
      membershipType: formData.membershipType,
      amount: currentConfig?.amount || "$0",
      mainContactName: formData.mainContactName,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      adults: formData.adults.map((adult) => ({
        ...adult,
        idProof: adult.idProof ? adult.idProof.name : null,
        studentIdProof: adult.studentIdProof ? adult.studentIdProof.name : null,
      })),
      kids: formData.kids,
      peopleCount: formData.adults.length + formData.kids.length,
      status: "Pending",
      createdAt: new Date().toLocaleDateString(),
    }; */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!accountHolder?.id) {
      alert("Please login again. Member account not found.");
      return;
    }

    if (!formData.membershipType) {
      alert("Please select a membership type.");
      return;
    }

    if (!currentConfig?.amount) {
      alert("Membership amount is missing.");
      return;
    }

    try {
      const payload = {
        member_id: Number(accountHolder.id),
        membership_type: formData.membershipType,
        amount: Number(String(currentConfig.amount).replace("$", "").trim()),
        notes: formData.message || "",
        main_contact_name: formData.mainContactName || "",
        email: formData.email || "",
        phone: formData.phone || "",
        adults: formData.adults.map((adult) => ({
          ...adult,
          idProof: adult.idProof ? adult.idProof.name : "",
          studentIdProof: adult.studentIdProof ? adult.studentIdProof.name : "",
        })),
        kids: formData.kids || [],
      };

      console.log("Submitting membership payload:", payload);

      //const result = await api.post("createMembershipApplication.php", payload);

      const endpoint = editMode
        ? "updateMembershipApplication.php"
        : "createMembershipApplication.php";

      const result = await api.post(endpoint, {
        ...payload,
        id: applicationId,
      });

      if (result?.success) {
        alert(
          editMode
            ? "Membership application updated successfully."
            : "Membership application submitted successfully."
        );
        navigate("/membershipdashboard");
      } else {
        alert(result?.message || "Failed to save membership application.");
      }
    } catch (error) {
      console.error("Membership submit error:", error);
      alert("Something went wrong while saving the application.");
    }
  };

  return (
    <section className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-brand mb-2">
            Membership Application
          </h1>
          <p className="text-gray-600">
            Create a membership application under your account.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-md p-6 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Main Contact Name
              </label>
              <input
                type="text"
                name="mainContactName"
                value={formData.mainContactName}
                onChange={handleBasicChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleBasicChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleBasicChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Membership Type
              </label>
              <select
                name="membershipType"
                value={formData.membershipType}
                onChange={handleBasicChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white"
              >
                <option value="">Select membership type</option>
                {Object.entries(membershipConfig).map(([key, item]) => (
                  <option key={key} value={key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {currentConfig && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4">
              <p className="text-green-800 font-medium">
                Selected: {currentConfig.label}
              </p>
            </div>
          )}

          {formData.adults.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-brand">Adult Details</h2>

              {formData.adults.map((adult, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4"
                >
                  <h3 className="font-semibold text-gray-800">
                    {formData.adults.length === 1
                      ? "Adult Applicant"
                      : `Adult ${index + 1}`}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={adult.fullName}
                      onChange={(e) =>
                        updateAdult(index, "fullName", e.target.value)
                      }
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />

                    <input
                      type="date"
                      value={adult.dob}
                      onChange={(e) => updateAdult(index, "dob", e.target.value)}
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />

                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={adult.phone}
                      onChange={(e) =>
                        updateAdult(index, "phone", e.target.value)
                      }
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />

                    <input
                      type="text"
                      placeholder="Relation to Account Holder"
                      value={adult.relation}
                      onChange={(e) =>
                        updateAdult(index, "relation", e.target.value)
                      }
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {currentConfig.requiresIdForPrimaryAdult && index === 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Valid ID Proof
                      </label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) =>
                          updateAdult(
                            index,
                            "idProof",
                            e.target.files?.[0] || null
                          )
                        }
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white"
                      />
                    </div>
                  )}

                  {currentConfig.requiresStudentId && index === 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Student ID Proof
                      </label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) =>
                          updateAdult(
                            index,
                            "studentIdProof",
                            e.target.files?.[0] || null
                          )
                        }
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(formData.membershipType === "Family Membership" ||
            formData.membershipType === "Family One Adult") && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-xl font-bold text-brand">Children Details</h2>
                  <button
                    type="button"
                    onClick={addChild}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition"
                  >
                    Add Child
                  </button>
                </div>

                {formData.kids.map((child, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800">
                        Child {index + 1}
                      </h3>

                      {formData.kids.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(index)}
                          className="text-sm text-red-500 hover:text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Child Full Name"
                        value={child.fullName}
                        onChange={(e) =>
                          updateChild(index, "fullName", e.target.value)
                        }
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      />

                      <input
                        type="date"
                        value={child.dob}
                        onChange={(e) => updateChild(index, "dob", e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Message or Notes
            </label>
            <textarea
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleBasicChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              placeholder="Optional notes for this membership application"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate("/membershipdashboard")}
              className="w-full sm:w-auto px-5 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              Back to Dashboard
            </button>

            <button
              type="submit"
              className="w-full sm:flex-1 bg-yellow-500 hover:bg-yellow-400 text-white font-semibold py-3 rounded-xl transition duration-300 shadow-lg"
            >
              {editMode ? "Update Membership Application" : "Submit Membership Application"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}